// Regra de vencimento do boleto da Vigor, no cadastro da UC (migration 20261104120000).
//
// Antes não havia onde guardar "este cliente paga todo dia 20": virava aviso de 12
// meses (39 UCs em 04/11/2026) e acabava quando os meses acabavam. Sem regra, o
// boleto vence no mesmo dia da conta da Equatorial — como sempre foi.
//
// "Dia fixo" segue o que o Faturamento já fazia à mão: o dia escolhido no mesmo mês
// do vencimento da Equatorial; se essa data já passou, o mesmo dia do mês seguinte.
//
// Datas em texto "AAAA-MM-DD", contas em UTC para o fuso não mudar o dia.
// Sem imports de runtime: roda sob `node --test`.

export type RegraVencimento = 'dia_fixo' | 'dias_apos_equatorial' | 'mes_seguinte';

export const REGRAS_VENCIMENTO: { valor: RegraVencimento; rotulo: string; pedeNumero: 'dia' | 'dias' | null }[] = [
    { valor: 'dia_fixo', rotulo: 'Dia fixo do mês', pedeNumero: 'dia' },
    { valor: 'dias_apos_equatorial', rotulo: 'Dias depois do vencimento da Equatorial', pedeNumero: 'dias' },
    { valor: 'mes_seguinte', rotulo: 'Mesmo dia da Equatorial, no mês seguinte', pedeNumero: null },
];

export interface RegraVencimentoUc {
    vencimento_regra?: string | null;
    vencimento_dia?: number | string | null;
}

const dois = (n: number) => String(n).padStart(2, '0');

function lerData(iso: string | null | undefined): { a: number; m: number; d: number } | null {
    const r = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso ?? ''));
    if (!r) return null;
    return { a: Number(r[1]), m: Number(r[2]) - 1, d: Number(r[3]) };
}

const ultimoDia = (a: number, m: number) => new Date(Date.UTC(a, m + 1, 0)).getUTCDate();

/** Dia `dia` do mês (ano, mês 0-11), sem passar do último dia: 31 em fevereiro vira 28/29. */
function noMes(a: number, m: number, dia: number): string {
    const ano = a + Math.floor(m / 12);
    const mes = ((m % 12) + 12) % 12;
    return `${ano}-${dois(mes + 1)}-${dois(Math.min(dia, ultimoDia(ano, mes)))}`;
}

const numeroDaRegra = (v: number | string | null | undefined): number | null => {
    const n = Number(v);
    return v === null || v === undefined || v === '' || !Number.isInteger(n) ? null : n;
};

/** Texto de erro, ou null quando a regra fecha. Regra vazia é válida (= igual à Equatorial). */
export function erroRegraVencimento(regra: string | null | undefined, numero: number | string | null | undefined): string | null {
    if (!regra) return null;
    const n = numeroDaRegra(numero);
    if (regra === 'dia_fixo') return n !== null && n >= 1 && n <= 31 ? null : 'Informe um dia entre 1 e 31.';
    if (regra === 'dias_apos_equatorial') return n !== null && n >= 1 && n <= 60 ? null : 'Informe de 1 a 60 dias.';
    if (regra === 'mes_seguinte') return null;
    return 'Regra de vencimento desconhecida.';
}

/**
 * Vencimento do boleto pela regra da UC. Null quando a UC não tem regra (vale o
 * vencimento da Equatorial) ou falta a data da Equatorial.
 */
export function vencimentoPelaRegra(uc: RegraVencimentoUc | null | undefined, vencimentoEquatorial: string | null | undefined, hoje: string): string | null {
    const regra = uc?.vencimento_regra;
    const eq = lerData(vencimentoEquatorial);
    if (!regra || !eq || erroRegraVencimento(regra, uc?.vencimento_dia)) return null;
    const n = numeroDaRegra(uc?.vencimento_dia) ?? 0;

    if (regra === 'dia_fixo') {
        const mesmoMes = noMes(eq.a, eq.m, n);
        return mesmoMes < hoje.slice(0, 10) ? noMes(eq.a, eq.m + 1, n) : mesmoMes;
    }
    if (regra === 'dias_apos_equatorial') {
        const d = new Date(Date.UTC(eq.a, eq.m, eq.d + n));
        return `${d.getUTCFullYear()}-${dois(d.getUTCMonth() + 1)}-${dois(d.getUTCDate())}`;
    }
    if (regra === 'mes_seguinte') return noMes(eq.a, eq.m + 1, eq.d);
    return null;
}

/** "Todo dia 20", para mostrar no cálculo e no cadastro. Null sem regra. */
export function textoRegraVencimento(uc: RegraVencimentoUc | null | undefined): string | null {
    const regra = uc?.vencimento_regra;
    if (!regra || erroRegraVencimento(regra, uc?.vencimento_dia)) return null;
    const n = numeroDaRegra(uc?.vencimento_dia);
    if (regra === 'dia_fixo') return `Todo dia ${n}`;
    if (regra === 'dias_apos_equatorial') return `${n} ${n === 1 ? 'dia' : 'dias'} depois do vencimento da Equatorial`;
    if (regra === 'mes_seguinte') return 'Mesmo dia do vencimento da Equatorial, no mês seguinte';
    return null;
}

/** Data de hoje no fuso do navegador, "AAAA-MM-DD". */
export function hojeIso(agora: Date = new Date()): string {
    return `${agora.getFullYear()}-${dois(agora.getMonth() + 1)}-${dois(agora.getDate())}`;
}
